import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { toaster } from '@/shared/ui/toaster.component';
import { validateEmail, normalizeEmail } from '@/shared/utils';
import {
  Button,
  Dialog,
  Field,
  Text,
} from '@noorden-insurtech/alba-design-system';
import { Mail, Plus, SquarePen, Trash2 } from 'lucide-react';
import { useReducer, useRef } from 'react';
import { useSendProposalEmail } from '../../../quotes/hooks/common/use-send-proposal-email.hook';
import { useProposalsSendEmail } from '../../hooks/use-proposals-send-email.hook';

function getEmailError(value: string, required = false): string | undefined {
  const trimmed = (value || '').trim();
  if (!trimmed) {
    return required ? 'E-mail é obrigatório' : undefined;
  }
  if (!validateEmail(trimmed)) {
    return 'E-mail inválido';
  }
  return undefined;
}

type AdditionalEmail = {
  id: string;
  email: string;
};

type EmailErrors = {
  emailCorrector?: string;
  emailProponent?: string;
  additionalEmails: Record<string, string | undefined>;
};

type State = {
  isEditingEmailCorrector: boolean;
  form: {
    emailCorrector: string;
    emailProponent: string;
    additionalEmails: AdditionalEmail[];
  };
  errors: EmailErrors;
};

type Agent = {
  email?: string;
  name?: string;
  commissionRate?: number;
  shareRate?: number;
};

export function ModalSendMail({
  productCode,
  policyId,
  emailCorrector = '',
  emailProponent = '',
  agents = [],
  disabled,
  disabledReason,
  stage = 'proposal',
}: {
  productCode: string;
  policyId: string;
  emailCorrector?: string;
  emailProponent?: string;
  agents?: Agent[];
  disabled?: boolean;
  /** Motivo exibido no `title` do botão quando desabilitado. */
  disabledReason?: string;
  stage?: 'proposal' | 'quote';
}) {
  const getInitialAdditionalEmails = (): AdditionalEmail[] => {
    return agents
      .slice(1)
      .filter(agent => agent.email)
      .map((agent, index) => ({
        id: `agent-${index}`,
        email: agent.email || '',
      }));
  };

  const initialErrors: EmailErrors = {
    emailCorrector: undefined,
    emailProponent: undefined,
    additionalEmails: {},
  };

  const [state, updateState] = useReducer(
    (
      state: State,
      action:
        | Partial<State>
        | { form?: Partial<State['form']>; errors?: Partial<EmailErrors> }
        | { type: 'RESET' }
    ): State => {
      if ('type' in action && action.type === 'RESET') {
        return {
          isEditingEmailCorrector: false,
          form: {
            emailCorrector,
            emailProponent,
            additionalEmails: getInitialAdditionalEmails(),
          },
          errors: initialErrors,
        };
      }
      return {
        ...state,
        ...action,
        form: {
          ...state.form,
          ...('form' in action ? action.form : {}),
        },
        errors: {
          ...state.errors,
          ...('errors' in action ? action.errors : {}),
          additionalEmails: {
            ...state.errors.additionalEmails,
            ...('errors' in action && action.errors?.additionalEmails
              ? action.errors.additionalEmails
              : {}),
          },
        },
      };
    },
    {
      isEditingEmailCorrector: false,
      form: {
        emailCorrector,
        emailProponent,
        additionalEmails: getInitialAdditionalEmails(),
      },
      errors: initialErrors,
    }
  );

  const isQuoteStage = stage === 'quote';
  const {
    mutateAsync: sendProposalEmail,
    isPending: isSendingProposal,
  } = useProposalsSendEmail(policyId);
  const {
    mutateAsync: sendQuoteEmail,
    isPending: isSendingQuote,
  } = useSendProposalEmail();
  // As duas mutations não podem ser unidas num ternário: os payloads diferem
  // no literal de `stage` ('quote' × 'proposal') e o TypeScript interseca os
  // parâmetros, deixando o campo como `never`. A escolha fica na chamada.
  const isPending = isQuoteStage ? isSendingQuote : isSendingProposal;

  const closeButtonRef = useRef<HTMLButtonElement>(null);

  function handleOpenChange(details: { open: boolean }) {
    if (details.open) {
      updateState({ type: 'RESET' });
    }
  }

  function handleAddEmail() {
    const newEmail: AdditionalEmail = {
      id: `additional-${Date.now()}`,
      email: '',
    };
    updateState({
      form: {
        additionalEmails: [...state.form.additionalEmails, newEmail],
      },
    });
  }

  function handleRemoveEmail(id: string) {
    const nextErrors = { ...state.errors.additionalEmails };
    delete nextErrors[id];
    updateState({
      form: {
        additionalEmails: state.form.additionalEmails.filter(
          (e: AdditionalEmail) => e.id !== id
        ),
      },
      errors: {
        additionalEmails: nextErrors,
      },
    });
  }

  function handleUpdateAdditionalEmail(id: string, email: string) {
    updateState({
      form: {
        additionalEmails: state.form.additionalEmails.map(
          (e: AdditionalEmail) => (e.id === id ? { ...e, email } : e)
        ),
      },
      errors: {
        additionalEmails: { [id]: undefined },
      },
    });
  }

  async function handleSendEmail() {
    const correctorError = getEmailError(state.form.emailCorrector);
    const proponentError = getEmailError(state.form.emailProponent);
    const additionalErrors: Record<string, string | undefined> = {};
    state.form.additionalEmails.forEach((e: AdditionalEmail) => {
      additionalErrors[e.id] = getEmailError(e.email, true);
    });

    const hasFieldErrors =
      Boolean(correctorError) ||
      Boolean(proponentError) ||
      Object.values(additionalErrors).some(Boolean);

    if (hasFieldErrors) {
      updateState({
        errors: {
          emailCorrector: correctorError,
          emailProponent: proponentError,
          additionalEmails: additionalErrors,
        },
      });
      toaster.create({
        type: 'error',
        title: 'Erro ao enviar e-mail',
        description: 'Verifique os e-mails informados.',
      });
      return;
    }

    const allEmails = [
      state.form.emailCorrector,
      state.form.emailProponent,
      ...state.form.additionalEmails.map((e: AdditionalEmail) => e.email),
    ];

    const emails = Array.from(
      new Set(allEmails.map((e: string) => normalizeEmail(e)).filter(Boolean))
    );

    if (emails.length === 0) {
      toaster.create({
        type: 'error',
        title: 'Erro ao enviar e-mail',
        description: 'Por favor, informe pelo menos um e-mail.',
      });
      return;
    }

    try {
      for (const email of emails) {
        if (isQuoteStage) {
          await sendQuoteEmail({ productCode, stage: 'quote', email, policyId });
        } else {
          await sendProposalEmail({
            productCode,
            stage: 'proposal',
            email,
            policyId,
          });
        }
      }

      const documentLabel = isQuoteStage ? 'cotação' : 'proposta';
      toaster.create({
        type: 'success',
        title:
          emails.length > 1
            ? 'E-mails enviados com sucesso'
            : 'E-mail enviado com sucesso',
        description: `O PDF da ${documentLabel} foi enviado para ${emails.length > 1 ? 'os e-mails informados' : 'o e-mail informado'}.`,
      });

      closeButtonRef.current?.click?.();
    } catch (error) {
      toaster.create({
        type: 'error',
        title: 'Erro ao enviar e-mail',
        description: error instanceof Error ? error.message : 'Erro inesperado',
      });
    }
  }

  return (
    <Dialog.Root onOpenChange={handleOpenChange}>
      <Dialog.Trigger asChild>
        <Button
          variant='secondary'
          size='md'
          className='flex items-center gap-2 rounded-xl'
          disabled={disabled}
          title={disabled ? disabledReason : undefined}
        >
          <Mail className='size-5' />
          E-mail
        </Button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content className='w-full max-w-lg'>
            <Dialog.Header>
              <Dialog.Title>
                <Mail size={32} className='text-alba-black-500' />
                Mandar por e-mail
              </Dialog.Title>
              <Dialog.CloseTrigger asChild>
                <Dialog.CloseButton />
              </Dialog.CloseTrigger>
            </Dialog.Header>
            <Dialog.Body>
              <Text>
                O PDF da {isQuoteStage ? 'cotação' : 'proposta'} será gerado
                para envio.
              </Text>
              <Text>
                Indique os e-mails que deverão receber a{' '}
                {isQuoteStage ? 'cotação' : 'proposta'}.
              </Text>

              <div className='flex flex-col gap-4 mt-3'>
                <Field.Root invalid={Boolean(state.errors.emailCorrector)}>
                  <Field.Label>E-mail Corretor</Field.Label>
                  <div className='relative'>
                    <Field.Input
                      type='email'
                      placeholder='exemplo@email.com'
                      className='pr-10'
                      value={state.form.emailCorrector}
                      onChange={e =>
                        updateState({
                          form: { emailCorrector: e.target.value.toLowerCase() },
                          errors: { emailCorrector: undefined },
                        })
                      }
                      onBlur={e =>
                        updateState({
                          errors: {
                            emailCorrector: getEmailError(e.target.value),
                          },
                        })
                      }
                      disabled={!state.isEditingEmailCorrector}
                      maxLength={FIELD_MAX_LENGTHS.email}
                    />
                    <button
                      type='button'
                      onClick={() =>
                        updateState({
                          isEditingEmailCorrector:
                            !state.isEditingEmailCorrector,
                        })
                      }
                      className='absolute right-3 top-1/2 transform -translate-y-1/2 text-alba-gray-500 hover:text-alba-gray-600 transition-colors'
                    >
                      <SquarePen className='size-5 text-alba-gray-500' />
                    </button>
                  </div>
                  {state.errors.emailCorrector && (
                    <Field.ErrorText>
                      {state.errors.emailCorrector}
                    </Field.ErrorText>
                  )}
                </Field.Root>

                <Field.Root invalid={Boolean(state.errors.emailProponent)}>
                  <Field.Label>E-mail Proponente</Field.Label>
                  <Field.Input
                    type='email'
                    placeholder='exemplo@email.com'
                    className='pr-10'
                    value={state.form.emailProponent}
                    onChange={e =>
                      updateState({
                        form: { emailProponent: e.target.value.toLowerCase() },
                        errors: { emailProponent: undefined },
                      })
                    }
                    onBlur={e =>
                      updateState({
                        errors: {
                          emailProponent: getEmailError(e.target.value),
                        },
                      })
                    }
                    maxLength={FIELD_MAX_LENGTHS.email}
                  />
                  {state.errors.emailProponent && (
                    <Field.ErrorText>
                      {state.errors.emailProponent}
                    </Field.ErrorText>
                  )}
                </Field.Root>

                {state.form.additionalEmails.map(
                  (additionalEmail: AdditionalEmail, index: number) => {
                    const additionalError =
                      state.errors.additionalEmails[additionalEmail.id];
                    return (
                      <Field.Root
                        key={additionalEmail.id}
                        invalid={Boolean(additionalError)}
                      >
                        <Field.Label>E-mail Adicional {index + 1}</Field.Label>
                        <div className='relative'>
                          <Field.Input
                            type='email'
                            placeholder='exemplo@email.com'
                            className='pr-10'
                            value={additionalEmail.email}
                            onChange={e =>
                              handleUpdateAdditionalEmail(
                                additionalEmail.id,
                                e.target.value.toLowerCase()
                              )
                            }
                            onBlur={e =>
                              updateState({
                                errors: {
                                  additionalEmails: {
                                    [additionalEmail.id]: getEmailError(
                                      e.target.value,
                                      true
                                    ),
                                  },
                                },
                              })
                            }
                            maxLength={FIELD_MAX_LENGTHS.email}
                          />
                          <button
                            type='button'
                            onClick={() =>
                              handleRemoveEmail(additionalEmail.id)
                            }
                            className='absolute right-3 top-1/2 transform -translate-y-1/2 text-alba-red-500 hover:text-alba-red-600 transition-colors'
                          >
                            <Trash2 className='size-5' />
                          </button>
                        </div>
                        {additionalError && (
                          <Field.ErrorText>{additionalError}</Field.ErrorText>
                        )}
                      </Field.Root>
                    );
                  }
                )}

                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  onClick={handleAddEmail}
                  className='flex items-center gap-2 self-start'
                >
                  <Plus className='size-4' />
                  Adicionar outro e-mail
                </Button>
              </div>
            </Dialog.Body>
            <Dialog.Footer>
              <Dialog.CloseTrigger asChild ref={closeButtonRef}>
                <Button variant='ghost' size='md'>
                  Cancelar
                </Button>
              </Dialog.CloseTrigger>
              <Button
                variant='primary'
                size='md'
                disabled={isPending}
                loading={isPending}
                onClick={handleSendEmail}
              >
                Enviar
              </Button>
            </Dialog.Footer>
          </Dialog.Content>
        </Dialog.Positioner>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
